import { useTranslation } from "react-i18next";
import { H4, Title1, SubTitle3, SubTitle2, Text } from "@components/new/Typo";
import Skeleton from "@components/new/Skeleton";
import { formatSumWithCurrency, formatNumber } from "@utils/formatValues";
import WalletIcon from "@assets/icons/outline/wallet.svg?react";
import styles from "./Calculation.module.scss";

interface CalculationProps {
  balance: number;
  totalWeightKg: number;
  pricePerKg: number;
  currency: string;
  amountDue: number;
  isInsufficient: boolean;
  isLoading?: boolean;
}

const Calculation = ({
  balance,
  totalWeightKg,
  pricePerKg,
  currency,
  amountDue,
  isInsufficient,
  isLoading,
}: CalculationProps) => {
  const { t } = useTranslation();
  const weightStr = formatNumber(totalWeightKg);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <H4 color="black">{t("fbpPage.pickUp.calc.title")}</H4>
        <SubTitle3 color="grey-800">
          {t("fbpPage.pickUp.calc.perKg", { price: formatSumWithCurrency(pricePerKg, currency) })}
        </SubTitle3>
      </div>

      <div className={styles.body}>
        <div className={styles.balanceCard}>
          <div className={styles.balanceLeft}>
            <WalletIcon className={styles.walletIcon} />
            <SubTitle3 className={styles.balanceLabel}>{t("fbpPage.pickUp.calc.balance")}</SubTitle3>
          </div>
          {isLoading ? (
            <Skeleton width="48px" height="14px" />
          ) : (
            <span className={styles.balanceValue}>{formatSumWithCurrency(balance, currency)}</span>
          )}
        </div>

        <div className={styles.row}>
          <SubTitle3 color="grey-800">
            {t("fbpPage.pickUp.calc.request", {
              weight: weightStr,
              price: formatSumWithCurrency(pricePerKg, currency),
            })}
          </SubTitle3>
          {isLoading ? (
            <Skeleton width="48px" height="14px" />
          ) : (
            <SubTitle2 color="black">{formatSumWithCurrency(amountDue, currency)}</SubTitle2>
          )}
        </div>
      </div>

      <div className={styles.divider} />

      <div className={styles.toPayRow}>
        <Title1 color="black">{t("fbpPage.pickUp.calc.toPay")}</Title1>
        <div className={styles.toPayValue}>
          {isLoading ? (
            <Skeleton width="56px" height="18px" />
          ) : (
            <>
              <Title1 color="black">{formatSumWithCurrency(amountDue, currency)}</Title1>
              {isInsufficient ? (
                <Text className={styles.short}>
                  {t("fbpPage.pickUp.calc.short", {
                    amount: formatSumWithCurrency(amountDue - balance, currency),
                  })}
                </Text>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calculation;
